import React from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useLoginResult } from "../State/LoginResultContext";


const plans = [
  {
    id: "single-site",
    title: "Single-Site Plan",
    bullets: [
      "One campus, venue, or location",
      "One live stream at a time",
      "Events, services, assemblies, or meeting",
    ],
    monthly: "$19",
    yearly: "$205.20",
    yearlyNote: "(save $22.80)",
    monthlyUrl: "https://buy.stripe.com/9B6dR95x4dK46zw2T52Nq0l",
    annuallyUrl: "https://buy.stripe.com/8x24gz6B849u9LI65h2Nq0m",
  },
  {
    id: "multi-site",
    title: "Multi-Site Plan",
    bullets: [
      "Multiple campuses or venues",
      "Create a team of users",
      "Up to three concurrent live streams",
    ],
    monthly: "$49",
    yearly: "$529.20",
    yearlyNote: "(save $58.80)",
    monthlyUrl: "https://buy.stripe.com/fZu00j1gOgWgbTQ0KX2Nq0n",
    annuallyUrl: "https://buy.stripe.com/aFa14ngbIfScaPM9ht2Nq0o",
  },
];

const maxBullets = Math.max(...plans.map((p) => p.bullets.length));

function PlanCard({ plan }) {
  return (
    <div className="w-full h-full flex flex-col rounded-2xl border border-white/30 bg-[#1A0232]/30 backdrop-blur-sm p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.04)]">
      <h2 className="text-2xl font-extrabold tracking-tight atkinson-font">
        {plan.title}
      </h2>

      <ul className="mt-4 space-y-2 text-white/90 atkinson-font">
        {plan.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <CheckIcon className="h-5 w-5 mt-0.5 shrink-0 text-white" />
            <span className="text-base tracking-tight">{b}</span>
          </li>
        ))}
        {Array.from({
          length: Math.max(0, maxBullets - plan.bullets.length),
        }).map((_, idx) => (
          <li
            // ensures consistent height so prices align across cards
            key={`placeholder-${plan.id}-${idx}`}
            aria-hidden="true"
            className="flex items-start gap-2 opacity-0 select-none pointer-events-none"
          >
            <CheckIcon className="h-5 w-5 mt-0.5 shrink-0 text-white" />
            <span className="text-base sm:text-lg tracking-tight">placeholder</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 space-y-1 atkinson-font">
        <div className="text-xl sm:text-xl font-bold tracking-tight">
          {plan.monthly}
          <span className="text-sm sm:text-base font-medium text-white/80 tracking-tight">
            /month
          </span>
        </div>
        <div className="text-xl sm:text-xl font-bold tracking-tight">
          {plan.yearly}
          <span className="text-sm sm:text-base font-medium text-white/80 tracking-tight">
            /year{" "}
          </span>
          <span className="text-sm sm:text-base font-medium text-white/70 tracking-tight">
            {plan.yearlyNote}
          </span>
        </div>
      </div>

      <div className="mt-auto pt-6">
        <div className="space-y-3">
          <a
            href={plan.monthlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3] no-underline"
            style={{ border: "2px solid #6A6680" }}
          >
            Choose Monthly
          </a>
          <a
            href={plan.annuallyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3] no-underline"
            style={{ border: "2px solid #6A6680" }}
          >
            Choose Annually
          </a>
        </div>

        <p className="mt-4 text-xs text-white/70 text-center atkinson-font tracking-tight">
          Auto-renewable. Cancel Anytime.
        </p>
      </div>
    </div>
  );
}

export default function ManagePlan() {
  const { loginResults } = useLoginResult();

  return (
    <div className="w-full">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4 sm:py-14">
        <div className="text-center">
          <h1 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight atkinson-font">
            Manage Plan
          </h1>

          <div className="mt-4">
            <div className="text-white atkinson-font tracking-tight">Current Plan</div>
            <div className="mt-2 inline-flex items-center justify-center rounded-xl border border-white/30 bg-[#1A0232]/30 px-8 py-3 text-2xl font-extrabold atkinson-font tracking-tight">
              { loginResults?.isMultisitePlan ? 'Multi Site' : loginResults?.isSinglesitePlan ? 'Single Site' : 'Free Trial' }
            </div>
          </div>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </div>
    </div>
  );
}

